// Links shown in the "Related tools" section at the bottom of a tool page.
// To add the section to another tool, add an entry to RELATED_LINKS for its page_copy id.

interface ToolLink {
  name: string;
  path: string;
  blurb: string;
}

interface GuideLink {
  title: string;
  path: string;
}

const TOOLS: Record<string, ToolLink> = {
  background_remover: {
    name: 'Background Remover',
    path: '/remove-background',
    blurb: 'Remove and replace backgrounds on up to 50 product photos at once.',
  },
  photo_studio: {
    name: 'Photo Studio',
    path: '/photo-studio',
    blurb: 'Turn phone photos into studio-quality white background product shots.',
  },
  image_generator: {
    name: 'Lifestyle Image Generator',
    path: '/lifestyle-image-generator',
    blurb: 'Place your product in a realistic lifestyle scene, with or without a model.',
  },
  image_resizer: {
    name: 'Image Resizer',
    path: '/image-resizer',
    blurb: 'Resize batches of images for your store, marketplaces, and social media.',
  },
  product_listing_agent: {
    name: 'Product Listing Agent',
    path: '/product-listing-agent',
    blurb: 'Create product images and a full SEO product listing from one photo.',
  },
  product_description: {
    name: 'Product Description Generator',
    path: '/product-description',
    blurb: 'Write SEO-friendly product descriptions from your photos and notes.',
  },
  blog_writer: {
    name: 'Blog Writer',
    path: '/blog-writer',
    blurb: 'Write SEO blog posts that feature and link to your products.',
  },
  social_media: {
    name: 'Social Media Generator',
    path: '/social-media',
    blurb: 'Create social media posts and captions for your products.',
  },
  ad_copy_generator: {
    name: 'Ad Copy Generator',
    path: '/ad-copy',
    blurb: 'Write ad copy for Google, Facebook, Amazon, and more.',
  },
};

const RELATED_LINKS: Record<string, { tools: string[]; guides: GuideLink[] }> = {
  background_remover: {
    tools: ['photo_studio', 'image_generator', 'image_resizer', 'product_listing_agent'],
    guides: [
      { title: 'How to create white background product photos without Photoshop', path: '/blog/white-background-product-photos' },
      { title: 'Popular AI image & video tools for ecommerce (2026)', path: '/blog/best-ai-image-video-tools-for-ecommerce-2026' },
      { title: 'Introducing the MerchantFloor x Shopify app', path: '/blog/merchant-floor-shopify-app' },
    ],
  },
  photo_studio: {
    tools: ['background_remover', 'image_generator', 'product_listing_agent', 'image_resizer'],
    guides: [
      { title: 'How to create white background product photos without Photoshop', path: '/blog/white-background-product-photos' },
      { title: 'Convert better by creating on-model images from your standard product shots', path: '/blog/ai-lifestyle-images-on-model' },
      { title: 'Popular AI image & video tools for ecommerce (2026)', path: '/blog/best-ai-image-video-tools-for-ecommerce-2026' },
    ],
  },
  image_generator: {
    tools: ['photo_studio', 'background_remover', 'product_listing_agent', 'image_resizer'],
    guides: [
      { title: 'Convert better by creating on-model images from your standard product shots', path: '/blog/ai-lifestyle-images-on-model' },
      { title: 'How to use AI to combine multiple product photos into a collection image', path: '/blog/how-to-use-ai-to-combine-multiple-product-photos-into-a-collection-image' },
      { title: 'Create banners with AI on the lifestyle images app', path: '/blog/create-banners-with-ai-on-the-lifestyle-images-app' },
    ],
  },
  product_listing_agent: {
    tools: ['photo_studio', 'image_generator', 'product_description', 'background_remover'],
    guides: [
      { title: 'Introducing the MerchantFloor x Shopify app', path: '/blog/merchant-floor-shopify-app' },
      { title: 'How to write a great product description (and mistakes to avoid)', path: '/blog/how-to-write-a-great-product-description-and-mistakes-to-avoid' },
      { title: 'How to get your store and products recommended by AI', path: '/blog/how-to-get-your-store-and-products-recommended-by-ai' },
    ],
  },
  blog_writer: {
    tools: ['product_description', 'social_media', 'ad_copy_generator', 'product_listing_agent'],
    guides: [
      { title: '5 reasons ecommerce brands should blog (and how to do it right)', path: '/blog/why-ecommerce-brands-should-blog-and-how-to-do-it-right' },
      { title: 'How to create a content calendar for your ecommerce business', path: '/blog/how-to-create-an-effective-content-calendar-for-your-ecommerce-business' },
      { title: 'The "5-hour per week" SEO plan for small ecommerce businesses', path: '/blog/the-5-hour-per-week-seo-plan-for-small-ecommerce-businesses' },
    ],
  },
  product_description: {
    tools: ['product_listing_agent', 'blog_writer', 'ad_copy_generator', 'photo_studio'],
    guides: [
      { title: 'How to write a great product description (and mistakes to avoid)', path: '/blog/how-to-write-a-great-product-description-and-mistakes-to-avoid' },
      { title: 'Why product descriptions really matter (and tips on making them better)', path: '/blog/why-product-descriptions-matter' },
      { title: 'How to write product descriptions for jewelry', path: '/blog/how-to-write-product-descriptions-for-jewelry' },
    ],
  },
};

export function getRelatedLinks(pageId: string): { tools: ToolLink[]; guides: GuideLink[] } | null {
  const entry = RELATED_LINKS[pageId];
  if (!entry) return null;
  return {
    tools: entry.tools.map((id) => TOOLS[id]).filter(Boolean),
    guides: entry.guides,
  };
}
